import { useState } from 'react';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';

import { formatCurrencyCompact } from '../../../../utils/currency';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';

interface DailyGoalRowProps {
  providerName: string;
  goal: number;
  scheduledProduction: number;
  completedProduction: number;
}

interface ProgressColors {
  scheduled: string;
  gap: string;
  completed: string;
}

const GREEN_THRESHOLD = 76;
const AMBER_THRESHOLD = 51;

export const DailyGoalRow = ({
  providerName,
  goal,
  scheduledProduction,
  completedProduction,
}: DailyGoalRowProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showNameTooltip, setShowNameTooltip] = useState(false);

  const handleNameHover = (e: React.PointerEvent<HTMLSpanElement>) => {
    const el = e.currentTarget;
    setShowNameTooltip(el.scrollWidth > el.clientWidth);
  };

  const scheduledPercent = goal > 0 ? (scheduledProduction / goal) * 100 : 0;
  const completedPercent = goal > 0 ? (completedProduction / goal) * 100 : 0;
  const scheduleGapPercent =
    goal > 0 ? ((goal - scheduledProduction) / goal) * 100 : 0;

  const scheduledWidth = Math.min(scheduledPercent, 100);
  const completedWidth = Math.min(completedPercent, 100);
  const scheduleGapWidth = Math.max(0, scheduleGapPercent);

  // Ensure completed bar always shows a tiny sliver when completed is 0
  const displayCompletedWidth = completedWidth > 0 ? completedWidth : 2;

  // Determine color based on schedule percent-to-goal thresholds
  // Green: 76-100%, Amber: 51-75%, Red: 0-50%
  const getProgressColor = (): ProgressColors => {
    if (scheduledPercent >= GREEN_THRESHOLD) {
      return {
        scheduled: 'vai-bg-emerald-300',
        gap: 'vai-bg-emerald-100',
        completed: 'vai-bg-emerald-800',
      };
    }
    if (scheduledPercent >= AMBER_THRESHOLD) {
      return {
        scheduled: 'vai-bg-amber-300',
        gap: 'vai-bg-amber-100',
        completed: 'vai-bg-amber-600',
      };
    }
    return {
      scheduled: 'vai-bg-red-300',
      gap: 'vai-bg-red-100',
      completed: 'vai-bg-red-800',
    };
  };

  const colors = getProgressColor();
  const scheduleGap = Math.max(0, goal - scheduledProduction);
  const showLegend = isExpanded;
  const showTooltip = isHovered && !isExpanded;

  const legendContent = (
    <div className="vai-flex vai-flex-col vai-gap-2">
      {/* Scheduled */}
      <div className="vai-flex vai-items-center vai-justify-between">
        <div className="vai-flex vai-items-center vai-gap-2">
          <div className={`vai-w-4 vai-h-4 vai-rounded ${colors.scheduled}`} />
          <span>{t`Scheduled`}</span>
        </div>
        <span className="vai-font-medium">
          {formatCurrencyCompact(scheduledProduction)}
        </span>
      </div>

      {/* Completed */}
      <div className="vai-flex vai-items-center vai-justify-between">
        <div className="vai-flex vai-items-center vai-gap-2">
          <div
            className={`vai-w-4 vai-h-2 vai-rounded-[2px] ${colors.completed}`}
          />
          <span>{t`Completed`}</span>
        </div>
        <span className="vai-font-medium">
          {formatCurrencyCompact(completedProduction)}
        </span>
      </div>

      {/* Schedule Gap */}
      <div className="vai-flex vai-items-center vai-justify-between">
        <div className="vai-flex vai-items-center vai-gap-2">
          <div className={`vai-w-4 vai-h-4 vai-rounded ${colors.gap}`} />
          <span>{t`Schedule Gap`}</span>
        </div>
        <span className="vai-font-medium">
          {formatCurrencyCompact(scheduleGap)}
        </span>
      </div>
    </div>
  );

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isExpanded) {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const toggleExpanded = () => {
    const isExpandedAfterToggle = !isExpanded;
    setIsExpanded(isExpandedAfterToggle);
    trackEvent(mixpanelEvents.dailyGoalRowClick, {
      context: 'DAILY_GOALS',
      expanded: isExpandedAfterToggle,
      providerName,
    });
  };

  const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    toggleExpanded();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggleExpanded();
    }
  };

  return (
    <div
      className="vai-flex vai-flex-col vai-gap-2 vai-p-2 vai-rounded vai-mb-1 vai-cursor-pointer hover:vai-bg-gray-50 vai-transition-colors"
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="vai-flex vai-items-center vai-justify-between vai-gap-2">
        <div className="vai-flex vai-items-center vai-gap-2 vai-flex-1 vai-min-w-0">
          <div className="vai-flex vai-items-center vai-flex-shrink-0 vai-text-muted-foreground">
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              className="vai-h-3 vai-w-3"
            />
          </div>
          <div className="vai-min-w-0 vai-flex-1 vai-overflow-hidden">
            <Tooltip delayDuration={50} eager open={showNameTooltip}>
              <TooltipTrigger asChild>
                <span
                  className="vai-block vai-truncate vai-font-inter vai-text-[14px] vai-font-normal vai-leading-[16px] vai-text-foreground"
                  onPointerEnter={handleNameHover}
                  onPointerLeave={() => setShowNameTooltip(false)}
                >
                  {providerName}
                </span>
              </TooltipTrigger>
              <TooltipContent>{providerName}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="vai-text-[18px] vai-font-inter vai-m-0 vai-font-normal vai-flex-shrink-0 vai-text-foreground">
          {formatCurrencyCompact(goal)}
        </div>
      </div>
      <div
        className="vai-relative vai-h-[16px] vai-bg-gray-100 vai-rounded vai-overflow-hidden vai-ml-[calc(0.75rem+0.5rem)]"
        onMouseEnter={() => !isExpanded && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseMove={handleMouseMove}
      >
        {scheduledWidth > 0 && (
          <div
            className={`vai-absolute vai-inset-y-0 vai-left-0 vai-rounded-l ${colors.scheduled}`}
            style={{
              width: `${scheduledWidth}%`,
            }}
          />
        )}
        {scheduleGapWidth > 0 && scheduledWidth < 100 && (
          <div
            className={`vai-absolute vai-inset-y-0 vai-rounded-r ${colors.gap}`}
            style={{
              left: `${scheduledWidth}%`,
              width: `${scheduleGapWidth}%`,
            }}
          />
        )}
        <div
          className={`vai-absolute vai-top-1/2 vai-left-0 vai-h-[6px] vai--translate-y-1/2 vai-rounded-r-[2px] ${colors.completed}`}
          style={{
            width: `${displayCompletedWidth}%`,
          }}
        />
      </div>
      {showTooltip && (
        <div
          className="vai-fixed vai-z-50 vai-bg-white vai-rounded vai-shadow-lg vai-p-3 vai-border vai-border-gray-200 vai-pointer-events-none"
          style={{
            left: `${tooltipPosition.x + 10}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translateY(-50%)',
          }}
        >
          <div className="vai-flex vai-flex-col vai-gap-2 vai-min-w-[200px] vai-font-inter vai-text-[13px] vai-font-normal vai-leading-none vai-text-foreground">
            {legendContent}
          </div>
        </div>
      )}
      {showLegend && (
        <div className="vai-flex vai-flex-col vai-gap-2 vai-pt-1 vai-ml-[calc(0.75rem+0.5rem)] vai-font-inter vai-text-[13px] vai-font-normal vai-leading-none vai-text-foreground">
          {legendContent}
        </div>
      )}
    </div>
  );
};
